/**
 * Touch: drag from a ground move's hotbar button to aim it (groundAim.ts). A press that moves past `START` pixels turns
 * into a virtual stick from the player (`FULL` pixels = the move's whole reach); lifting the finger casts, lifting it
 * back on the button cancels. A press that never moves is the usual tap (at the foe's feet, or ahead).
 * Mouse and pen presses are left to the button's own handler.
 */
import { aimBegin, aimDrag, aimEnd, aimTap, groundMoveAt } from '../groundAim';

const START = 14;
const FULL = 110;

/** Wires the drag on one hotbar button; call before the button's own press handler is added. */
export function groundDrag(b: HTMLElement, slot: number): void {
  let drag: { id: number; x: number; y: number; aiming: boolean } | null = null;
  b.addEventListener('pointerdown', (e) => {
    if (e.pointerType !== 'touch' || !groundMoveAt(slot)) return;
    // Ours, not the button's press (that would cast at once).
    e.stopImmediatePropagation();
    e.preventDefault();
    drag = { id: e.pointerId, x: e.clientX, y: e.clientY, aiming: false };
    b.setPointerCapture?.(e.pointerId);
  });
  b.addEventListener('pointermove', (e) => {
    if (!drag || e.pointerId !== drag.id) return;
    const dx = e.clientX - drag.x;
    const dy = e.clientY - drag.y;
    if (!drag.aiming) {
      if (Math.hypot(dx, dy) < START) return;
      drag.aiming = aimBegin(slot);
      if (!drag.aiming) return;
    }
    aimDrag(dx, dy, FULL);
  });
  const end = (e: PointerEvent, cancelled: boolean): void => {
    if (!drag || e.pointerId !== drag.id) return;
    const d = drag;
    drag = null;
    if (!d.aiming) {
      if (!cancelled) aimTap(slot);
      return;
    }
    const r = b.getBoundingClientRect();
    const onButton = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
    aimEnd(!cancelled && !onButton);
  };
  b.addEventListener('pointerup', (e) => end(e, false));
  b.addEventListener('pointercancel', (e) => end(e, true));
}
